import type { ContentPage } from "./types";

const norm = (s: string) => s.replace(/^\/+|\/+$/g, "");

export interface RegistryOptions {
  /** URL prefix all pages sit under, e.g. "/hrms-software". Used to build
   *  absolute URLs for the sitemap and nav. No trailing slash. */
  basePath: string;
}

export type ContentRegistry = ReturnType<typeof createRegistry>;

// Turns a list of ContentPages into a slug-keyed registry used by the
// catch-all routes (lookup + static params), the content sitemap, and nav.
export function createRegistry(pages: ContentPage[], options: RegistryOptions) {
  const basePath = options.basePath.replace(/\/+$/g, "");
  const bySlug = new Map<string, ContentPage>(
    pages.map((p) => [norm(p.slug), p])
  );

  return {
    /** URL prefix these pages sit under (e.g. "/hrms-software"). */
    basePath,
    /** Resolve a Next catch-all param (string[]) to a page, or null. */
    get(slugParts: string[] | undefined): ContentPage | null {
      return bySlug.get((slugParts || []).join("/")) || null;
    },
    /** For generateStaticParams: every page's slug as a string[]. */
    allParams(): { slug: string[] }[] {
      return pages.map((p) => ({ slug: norm(p.slug).split("/") }));
    },
    /** Every page in this registry (source order). */
    all(): ContentPage[] {
      return pages;
    },
    /** Absolute site path for a page, e.g. "/hrms-software/expense-management/". */
    urlFor(page: ContentPage): string {
      return `${basePath}/${norm(page.slug)}/`;
    },
  };
}
